import React, { useEffect, useState } from 'react';

const Loading = ({ 
  text = "Loading...",
  size = "medium", // 'small', 'medium', 'large'
  color = "#FF660E",
  showText = true
}) => {
  const [dots, setDots] = useState([0, 0, 0]);
  
  const sizeConfig = {
    small: { dotSize: 8, textSize: 'text-sm', speed: 600 },
    medium: { dotSize: 12, textSize: 'text-base', speed: 500 },
    large: { dotSize: 16, textSize: 'text-lg', speed: 400 }
  };

  const { dotSize, textSize, speed } = sizeConfig[size];

  useEffect(() => {
    let animationFrame;
    
    const animate = (time) => {
      // Bounce animation for three dots
      const bounce = (t, offset) => {
        const progress = ((time * 0.001) + offset) % 1;
        return Math.sin(progress * Math.PI * 2) * 0.5 + 0.5;
      };

      setDots([
        bounce(time, 0),
        bounce(time, 0.33),
        bounce(time, 0.66)
      ]);
      
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [speed]);

  return (
    <div className="flex flex-col items-center justify-center">
      {/* Bouncing Dots */}
      <div className="flex items-center space-x-2">
        {dots.map((scale, index) => (
          <div
            key={index}
            className="rounded-full"
            style={{
              width: `${dotSize}px`,
              height: `${dotSize}px`,
              backgroundColor: color,
              transform: `scale(${0.3 + scale * 0.7})`,
              opacity: 0.3 + scale * 0.7,
              boxShadow: `0 0 ${dotSize * 0.5}px ${color}`,
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </div>

      {/* Loading text */}
      {showText && (
        <p 
          className={`mt-4 font-medium ${textSize} animate-pulse`}
          style={{ color }}
        >
          {text}
        </p>
      )}
    </div>
  );
};

// Alternative: Wave Loading Animation
export const WaveLoading = ({ 
  text = "Loading...",
  size = "medium",
  color = "#FF660E",
  showText = true
}) => {
  const [scales, setScales] = useState([1, 1, 1]);
  
  const sizeConfig = {
    small: { dotSize: 8, textSize: 'text-sm', speed: 800 },
    medium: { dotSize: 12, textSize: 'text-base', speed: 700 },
    large: { dotSize: 16, textSize: 'text-lg', speed: 600 }
  };

  const { dotSize, textSize, speed } = sizeConfig[size];

  useEffect(() => {
    let animationFrame;
    
    const animate = (time) => {
      const wave = (t, offset) => {
        const progress = ((time * 0.001) + offset) % 1;
        return Math.sin(progress * Math.PI) * 0.8 + 1;
      };

      setScales([
        wave(time, 0),
        wave(time, 0.33),
        wave(time, 0.66)
      ]);
      
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [speed]);

  return (
    <div className="flex flex-col items-center justify-center">
      {/* Wave Dots */}
      <div className="flex items-center space-x-3">
        {scales.map((scale, index) => (
          <div
            key={index}
            className="rounded-full"
            style={{
              width: `${dotSize}px`,
              height: `${dotSize}px`,
              backgroundColor: color,
              transform: `scale(${scale})`,
              opacity: 0.4 + (scale - 1) * 0.6,
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </div>

      {/* Loading text */}
      {showText && (
        <p 
          className={`mt-4 font-medium ${textSize}`}
          style={{ color }}
        >
          {text}
        </p>
      )}
    </div>
  );
};

// Alternative: Pulse Loading Animation
export const PulseLoading = ({ 
  text = "Loading...",
  size = "medium",
  color = "#FF660E",
  showText = true
}) => {
  const [pulse, setPulse] = useState(0);
  
  const sizeConfig = {
    small: { dotSize: 6, textSize: 'text-sm', speed: 1200 },
    medium: { dotSize: 8, textSize: 'text-base', speed: 1000 },
    large: { dotSize: 10, textSize: 'text-lg', speed: 800 }
  };

  const { dotSize, textSize, speed } = sizeConfig[size];

  useEffect(() => {
    const interval = setInterval(() => {
      setPulse(prev => (prev + 1) % 3);
    }, speed / 3);

    return () => clearInterval(interval);
  }, [speed]);

  return (
    <div className="flex flex-col items-center justify-center">
      {/* Pulsing Dots */}
      <div className="flex items-center space-x-1">
        {[0, 1, 2].map(index => (
          <div
            key={index}
            className="rounded-full transition-all duration-300"
            style={{
              width: `${dotSize}px`,
              height: `${dotSize}px`,
              backgroundColor: color,
              transform: `scale(${pulse === index ? 1.2 : 1})`,
              opacity: pulse === index ? 1 : 0.3
            }}
          />
        ))}
      </div>

      {/* Loading text */}
      {showText && (
        <p 
          className={`mt-4 font-medium ${textSize}`}
          style={{ color }}
        >
          {text}
        </p>
      )}
    </div>
  );
};

export default Loading;